import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { type Patient, patientResponseSchema, patientsResponseSchema } from "../types";

export function getPatientQueryKey(params: { workplaceId: string; patientId?: string }) {
  return ["home-health", params.workplaceId, "patients", params.patientId];
}

const PATIENT_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/:workplaceId/patients/:patientId`;

export function usePatient(
  patientId?: string,
  options: UseQueryOptions<Patient, unknown, Patient> = {}
) {
  const workplaceId = useCurrentUserId() ?? "";

  return useQuery({
    queryKey: getPatientQueryKey({ workplaceId, patientId }),
    queryFn: async () => {
      const response = await get({
        url: PATIENT_URL.replace(":workplaceId", workplaceId).replace(":patientId", patientId!),
        responseSchema: patientResponseSchema,
      });

      return response.data.data;
    },
    retry: 0,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 10,
    enabled: isDefined(patientId),
    ...options,
  });
}

export function useExternalPatientIdentifiers() {
  const workplaceId = useCurrentUserId() ?? "";

  return useQuery({
    queryKey: ["home-health", workplaceId, "patients", "external-identifiers"],
    queryFn: async () => {
      const response = await get({
        url: PATIENT_URL.replace(":workplaceId", workplaceId).replace(":patientId", ""),
        responseSchema: patientsResponseSchema,
      });

      const allIdentifiers = response.data.data.map(
        (patient) => patient.attributes.externalPatientIdentifier
      );

      // Remove duplicates. Current TS targets prevent using Set() here.
      return allIdentifiers.filter((value, index, array) => array.indexOf(value) === index);
    },
  });
}
